import React from "react";
import { useParams } from "react-router-dom";
import transmissionsStyles from "../../components/Report/TransmissionsTable.module.css";

type QuestionType = "scale" | "single_option" | "multiple_option";

export interface TransmissionQuestion {
  _id: string;
  questionSequence: number;
  question: string;
  answer: string;
  questionType: QuestionType;
  option: string | string[];
  questionDateInStringFormat: string;
  questionSendTimeStamp?: number;
}

interface TransmissionsPageProps {
  customHeight?: string;
  customWidth?: string;
  patientId?: string;
  transmissionData: {
    data: TransmissionQuestion[];
    msg?: string;
    status?: string;
  } | null;
  isLoading: boolean;
  error: string | null;
  startingIndex?: number;
}

const formatAnswer = (question: TransmissionQuestion): string => {
  return question.answer || "N/A";
};

const formatQuestionText = (
  question: TransmissionQuestion,
  index: number,
  startingIndex: number
): JSX.Element => {
  const { question: questionText, option } = question;

  return (
    <div className={transmissionsStyles.questionContainer}>
      <div className={transmissionsStyles.questionText}>
        {startingIndex + index + 1}. {questionText}
      </div>
      {Array.isArray(option) && option.length > 0 && (
        <div className={transmissionsStyles.optionsContainer}>
          {option.map((opt, idx) => (
            <div key={idx} className={transmissionsStyles.optionItem}>
              <span className={transmissionsStyles.optionNumber}>
                {idx + 1}.
              </span>
              <span className={transmissionsStyles.optionText}>{opt}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const formatDate = (timestamp?: number): string => {
  if (!timestamp) return "N/A";

  try {
    const date = new Date(timestamp * 1000);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid date";
  }
};

const NewTransmissionsPage: React.FC<TransmissionsPageProps> = ({
  customHeight = "10in",
  patientId: propPatientId,
  transmissionData,
  isLoading,
  error,
  startingIndex = 0,
  customWidth,
}) => {
  const { patientId: paramPatientId } = useParams<{ patientId: string }>();
  const patientId = propPatientId || paramPatientId || "";

  return (
    <div
      className={transmissionsStyles.content}
      style={{ height: customHeight, width: customWidth }}
    >
      <div className={transmissionsStyles.pageContainer}>
        <div className={transmissionsStyles.contentInner}>
          <div className={transmissionsStyles.sectionHeader}>TRANSMISSIONS</div>

          <div className={transmissionsStyles.tableWrapper}>
            <div className={transmissionsStyles.tableScrollContainer}>
              <table className={transmissionsStyles.transmissionsTable}>
                <thead>
                  <tr className={transmissionsStyles.tableHeader}>
                    <th
                      className={transmissionsStyles.tableHeaderCell}
                      style={{ width: "50%" }}
                    >
                      Question
                    </th>
                    <th
                      className={`${transmissionsStyles.tableHeaderCell} ${transmissionsStyles.tableHeaderCellCenter}`}
                      style={{ width: "30%" }}
                    >
                      Answer
                    </th>
                    <th
                      className={`${transmissionsStyles.tableHeaderCell} ${transmissionsStyles.tableHeaderCellCenter} ${transmissionsStyles.tableHeaderCellLast}`}
                      style={{ width: "20%" }}
                    >
                      Answer Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={3} className={transmissionsStyles.noData}>
                        Loading transmissions...
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td
                        colSpan={3}
                        className={`${transmissionsStyles.noData} ${transmissionsStyles.error}`}
                      >
                        Error: {error}
                      </td>
                    </tr>
                  ) : transmissionData?.data &&
                    transmissionData.data.length > 0 ? (
                    transmissionData.data.map((question, index) => (
                      <tr
                        key={question._id}
                        className={`${transmissionsStyles.tableRow} ${
                          index % 2 === 1
                            ? transmissionsStyles.tableRowEven
                            : ""
                        }`}
                      >
                        <td className={transmissionsStyles.tableCell}>
                          {formatQuestionText(question, index, startingIndex)}
                        </td>
                        <td
                          className={`${transmissionsStyles.tableCell} ${transmissionsStyles.tableCellCenter}`}
                        >
                          {formatAnswer(question)}
                        </td>
                        <td
                          className={`${transmissionsStyles.tableCell} ${transmissionsStyles.tableCellCenter} ${transmissionsStyles.tableCellLast}`}
                        >
                          {formatDate(question.questionSendTimeStamp)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className={transmissionsStyles.noData}>
                        No Transmissions Available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewTransmissionsPage;
